interface GeocodeResult {
  lat: string;
  lon: string;
  timezone: string | null;
}

export interface ReverseGeocodeResult {
  country: string;
  city: string;
}

export async function geocodeCityCountry(city: string, country: string): Promise<GeocodeResult | null> {
  const q = [city, country].filter(Boolean).join(', ');
  if (!q.trim()) return null;

  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
  const res = await fetch(url, {
    headers: { 'Accept-Language': 'en', 'User-Agent': 'EmployeeDirectory/1.0' },
  });
  if (!res.ok) return null;

  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return null;

  const lat = parseFloat(data[0].lat).toFixed(6);
  const lon = parseFloat(data[0].lon).toFixed(6);

  // Fetch timezone from coordinates
  let timezone: string | null = null;
  try {
    const tzRes = await fetch(
      `https://timeapi.io/api/timezone/coordinate?latitude=${lat}&longitude=${lon}`
    );
    if (tzRes.ok) {
      const tzData = await tzRes.json();
      timezone = tzData.timeZone ?? null;
    }
  } catch {
    // timezone lookup is best-effort
  }

  return { lat, lon, timezone };
}

/**
 * Reverse geocodes a coordinate pair using Nominatim.
 * Returns the resolved country and nearest city/town, or null if the
 * coordinates do not fall within any country (e.g. open ocean).
 */
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodeResult | null> {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`;
  try {
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'EmployeeDirectory/1.0' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const country: string | undefined = data?.address?.country;
    if (!country) return null;
    const city: string =
      data?.address?.city ??
      data?.address?.town ??
      data?.address?.village ??
      data?.address?.county ??
      '';
    return { country, city };
  } catch {
    return null;
  }
}
